import { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import config from "../config";
import { AuthContext } from "../AuthProvider";

const CACHE_KEY = "lumen.backendVersion";
const CACHE_TTL_MS = 10 * 60 * 1000;

const normalizeVersion = (value) => {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed.length > 80 ? trimmed.slice(0, 80) : trimmed;
  }
  if (typeof value === "number") return String(value);
  return null;
};

const extractVersion = (data) => {
  const direct = normalizeVersion(data);
  if (direct) return direct;

  if (!data || typeof data !== "object") return null;

  const candidates = [
    data.version,
    data.appVersion,
    data.buildVersion,
    data?.build?.version,
    data?.build?.appVersion,
    data?.build?.buildVersion,
    data?.git?.commit?.id,
    data?.git?.commitId,
    data?.commit,
    data?.revision,
  ];

  for (const candidate of candidates) {
    const v = normalizeVersion(candidate);
    if (v) return v;
  }

  return null;
};

const readCache = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.version || !parsed?.ts) return null;
    if (Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return String(parsed.version);
  } catch {
    return null;
  }
};

const writeCache = (version) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ version, ts: Date.now() })
    );
  } catch {
    // ignore
  }
};

export default function useBackendVersion({ enabled = true } = {}) {
  const { keycloak } = useContext(AuthContext);
  const token = keycloak?.token;

  const cached = useMemo(() => readCache(), []);
  const [version, setVersion] = useState(cached);
  const [loading, setLoading] = useState(enabled && !cached && !!token);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const base = String(config?.apiBaseUrl || "").replace(/\/+$/, "");
    const paths = [
      process.env.REACT_APP_BACKEND_VERSION_PATH,
      "/version",
      "/info",
      "/actuator/info",
    ]
      .filter(Boolean)
      .map((p) => String(p))
      .map((p) => (p.startsWith("/") ? p : `/${p}`));

    const fetchVersion = async () => {
      setLoading(true);

      for (const path of paths) {
        try {
          const res = await axios.get(`${base}${path}`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: false,
          });
          if (cancelled) return;

          const v = extractVersion(res?.data);
          if (v) {
            setVersion(v);
            writeCache(v);
            setLoading(false);
            return;
          }
        } catch (e) {
          if (cancelled) return;
          // try next path
          if (e?.response && e.response.status !== 404) {
            // non-404 means endpoint exists but request failed; don't spam more
            break;
          }
        }
      }

      if (cancelled) return;
      setLoading(false);
    };

    fetchVersion();
    const interval = setInterval(fetchVersion, CACHE_TTL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [enabled, token]);

  return { version, loading };
}
